import React, { useRef, useState, useEffect } from 'react';
import JSZip from 'jszip';
import '../styles/PhotoResult.css';
import { useParams } from 'react-router-dom';

const PhotoResult = ({ photos: initialPhotos, layout: initialLayout, gridCount: initialGridCount, allPhotosHistory: initialAllPhotosHistory, onDownload, indukName: initialIndukName, recordedVideos: initialRecordedVideos }) => {
  const { sessionId } = useParams();
  const resultContainerRef = useRef(null);
  const [showGallery, setShowGallery] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle | uploading | done | error
  const [showVideoGrid, setShowVideoGrid] = useState(false);
  const [countdown, setCountdown] = useState(null); // countdown timer untuk refresh
  const [hasUploaded, setHasUploaded] = useState(false); // untuk memastikan upload hanya sekali
  const [uploadAttempted, setUploadAttempted] = useState(false); // untuk mencegah multiple attempts
  const [countdownStarted, setCountdownStarted] = useState(false); // untuk mencegah multiple countdown timers
  const [uploadComplete, setUploadComplete] = useState(false); // untuk menandai upload sudah selesai
  const [loaded, setLoaded] = useState(false);
  // Main session/photo state
  const [photos, setPhotos] = useState(initialPhotos || []);
  const [layout, setLayout] = useState(initialLayout || null);
  const [gridCount, setGridCount] = useState(initialGridCount || null);
  const [allPhotosHistory, setAllPhotosHistory] = useState(initialAllPhotosHistory || []);
  const [indukName, setIndukName] = useState(initialIndukName || sessionId || null);
  const [recordedVideos, setRecordedVideos] = useState(initialRecordedVideos || []);

  // Load upload state dari localStorage saat komponen mount
  useEffect(() => {
    if (!indukName) return;
    
    try {
      const savedUploadStatus = localStorage.getItem(`uploadStatus_${indukName}`);
      const savedHasUploaded = localStorage.getItem(`hasUploaded_${indukName}`);
      const savedUploadAttempted = localStorage.getItem(`uploadAttempted_${indukName}`);
      const savedUploadComplete = localStorage.getItem(`uploadComplete_${indukName}`);
      const savedCountdown = localStorage.getItem(`countdown_${indukName}`);
      const savedCountdownStarted = localStorage.getItem(`countdownStarted_${indukName}`);
      
      console.log('Loading upload state from localStorage:', {
        savedUploadStatus,
        savedHasUploaded,
        savedUploadAttempted,
        savedUploadComplete,
        savedCountdown,
        savedCountdownStarted
      });
      
      if (savedUploadStatus) {
        setUploadStatus(savedUploadStatus);
      }
      if (savedHasUploaded === 'true') {
        setHasUploaded(true);
      }
      if (savedUploadAttempted === 'true') {
        setUploadAttempted(true);
      }
      if (savedUploadComplete === 'true') {
        setUploadComplete(true);
      }
      if (savedCountdown) {
        const countdownValue = parseInt(savedCountdown);
        setCountdown(countdownValue);
        console.log('Loaded countdown from localStorage:', countdownValue);
      }
      if (savedCountdownStarted === 'true') {
        setCountdownStarted(true);
      }
    } catch (error) {
      console.warn('Error loading upload state from localStorage:', error);
    }
  }, [indukName]);

    // Restart countdown timer jika upload sudah selesai
  useEffect(() => {
    if (uploadStatus === 'done' && countdown !== null && countdown > 0 && !countdownStarted && !window.countdownTimerRef && uploadComplete) {
      console.log('Starting countdown timer with', countdown, 'seconds remaining');
      setCountdownStarted(true);
      
      // Simpan countdown started ke localStorage
      try {
        localStorage.setItem(`countdownStarted_${indukName}`, 'true');
      } catch (error) {
        console.warn('Error saving countdown started to localStorage:', error);
      }
      
      // Delay kecil untuk memastikan state sudah stabil
      const timer = setTimeout(() => {
        console.log('Countdown timer started with', countdown, 'seconds');
        
        // Cleanup existing timer if any
        if (window.countdownTimerRef) {
          console.log('Cleaning up existing countdown timer');
          clearInterval(window.countdownTimerRef);
          window.countdownTimerRef = null;
        }
        
        const countdownTimer = setInterval(() => {
          setCountdown(prev => {
            const newCountdown = prev - 1;
            console.log('Countdown:', newCountdown);
            
            // Simpan countdown ke localStorage
            try {
              localStorage.setItem(`countdown_${indukName}`, newCountdown.toString());
            } catch (error) {
              console.warn('Error saving countdown to localStorage:', error);
            }
            
            if (newCountdown <= 0) {
              clearInterval(countdownTimer);
              // Cleanup localStorage
              try {
                localStorage.removeItem(`uploadStatus_${indukName}`);
                localStorage.removeItem(`hasUploaded_${indukName}`);
                localStorage.removeItem(`uploadAttempted_${indukName}`);
                localStorage.removeItem(`uploadComplete_${indukName}`);
                localStorage.removeItem(`countdown_${indukName}`);
                localStorage.removeItem(`countdownStarted_${indukName}`);
              } catch (error) {
                console.warn('Error cleaning up localStorage:', error);
              }
              console.log('Countdown finished, refreshing page...');
              window.location.reload();
            }
            return newCountdown;
          });
        }, 1000);
        
        // Store timer reference untuk cleanup
        window.countdownTimerRef = countdownTimer;
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [uploadStatus, countdown, countdownStarted, uploadComplete, indukName]);

  // Cleanup timer saat komponen unmount
  useEffect(() => {
    return () => {
      if (window.countdownTimerRef) {
        console.log('Cleaning up countdown timer on unmount');
        clearInterval(window.countdownTimerRef);
        window.countdownTimerRef = null;
      }
    };
  }, []);

  // Handle video grid display untuk smooth autoplay
  useEffect(() => {
    if (showVideoGrid && recordedVideos && recordedVideos.some(v => v)) {
      // Delay kecil untuk memastikan DOM sudah ter-render
      const timer = setTimeout(() => {
        const videoElements = document.querySelectorAll('.result-photo video');
        let loadedVideos = 0;
        const totalVideos = videoElements.length;
        
        // Fungsi untuk memulai semua video setelah semua loaded
        const startAllVideos = () => {
          videoElements.forEach((video, index) => {
            if (video && recordedVideos[index]) {
              // Set video properties untuk smooth playback
              video.currentTime = 0;
              video.playbackRate = 1.0;
              video.volume = 0; // Muted
              
              // Optimize video performance
              video.style.willChange = 'transform, opacity';
              
              // Tambahkan event listener untuk smooth loop
              const loopHandler = () => {
                video.currentTime = 0;
                video.play().catch(err => console.log(`Video ${index + 1} loop failed:`, err));
              };
              
              // Store reference untuk cleanup
              video.loopHandler = loopHandler;
              video.addEventListener('ended', loopHandler);
              
              // Play video dengan error handling
              video.play().catch(err => {
                console.log(`Video ${index + 1} auto play failed:`, err);
                // Retry play setelah delay
                setTimeout(() => {
                  video.play().catch(retryErr => console.log(`Video ${index + 1} retry failed:`, retryErr));
                }, 1000);
              });
            }
          });
        };

        // Track loading progress
        videoElements.forEach((video, index) => {
          if (video && recordedVideos[index]) {
            const onLoaded = () => {
              loadedVideos++;
              if (loadedVideos === totalVideos) {
                // Semua video sudah loaded, mulai play
                startAllVideos();
              }
            };
            
            if (video.readyState >= 2) {
              // Video sudah loaded
              onLoaded();
            } else {
              // Tunggu video load
              video.addEventListener('loadeddata', onLoaded, { once: true });
            }
          }
        });
      }, 500);

      return () => {
        clearTimeout(timer);
        // Cleanup video event listeners
        const videoElements = document.querySelectorAll('.result-photo video');
        videoElements.forEach(video => {
          video.removeEventListener('ended', video.loopHandler);
        });
      };
    }
  }, [showVideoGrid, recordedVideos]);

  const getLayoutClass = () => {
    switch (gridCount) {
      case 1:
        return layout === 1 ? 'layout-single-centered' : 'layout-single-top';
      case 2:
        if (layout === 1) return 'layout-vertical-center';
        if (layout === 2) return 'layout-vertical-top';
        if (layout === 3) return 'layout-vertical-gap';
        return '';
      case 3:
        if (layout === 1) return 'layout-vertical-center-3';
        if (layout === 2) return 'layout-vertical-ends';
        return '';
      case 4:
        if (layout === 1) return 'layout-vertical-center-4';
        if (layout === 2) return 'layout-2-top-2-bottom';
        if (layout === 3) return 'layout-mix-center';
        return '';
      default:
        return '';
    }
  };

  const handleDownload = async () => {
    try {
      // Buat canvas untuk menggambar grid foto
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set ukuran canvas sesuai dengan container
      const container = resultContainerRef.current;
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      
      // Gambar background putih
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Gambar setiap foto ke canvas
      const photoElements = container.querySelectorAll('.result-photo img');
      const photoPromises = Array.from(photoElements).map(img => {
        return new Promise((resolve) => {
          const tempImg = new Image();
          tempImg.crossOrigin = 'anonymous';
          tempImg.onload = () => {
            const rect = img.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            
            // Hitung posisi relatif terhadap container
            const x = (rect.left - containerRect.left) * (canvas.width / containerRect.width);
            const y = (rect.top - containerRect.top) * (canvas.height / containerRect.height);
            const width = rect.width * (canvas.width / containerRect.width);
            const height = rect.height * (canvas.height / containerRect.height);
            
            // Simpan state canvas
            ctx.save();
            
            // Terapkan transformasi mirror
            ctx.translate(x + width, y);
            ctx.scale(-1, 1);
            
            // Gambar foto dengan posisi yang sudah di-mirror
            ctx.drawImage(tempImg, 0, 0, width, height);
            
            // Kembalikan state canvas
            ctx.restore();
            
            resolve();
          };
          tempImg.src = img.src;
        });
      });

      // Tunggu semua foto selesai digambar
      await Promise.all(photoPromises);

      // Konversi canvas ke blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95));
      
      // Buat link download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `photo-grid-${new Date().getTime()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Panggil callback onDownload jika ada
      if (onDownload) {
        onDownload();
      }
    } catch (error) {
      console.error('Error downloading photo grid:', error);
      alert('Gagal mengunduh foto. Silakan coba lagi.');
    }
  };

  // Fungsi hash sederhana untuk string (base64)
  function simpleHash(str) {
    let hash = 0, i, chr;
    if (str.length === 0) return hash;
    for (i = 0; i < str.length; i++) {
      chr = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }

  const handleDownloadAllZip = async () => {
    try {
      const zip = new JSZip();
      // Simpan hanya hasil akhir (foto terakhir) dari setiap slot (index)
      const lastPhotoPerIndex = {};
      allPhotosHistory.forEach(item => {
        if (item.photo) {
          lastPhotoPerIndex[item.index] = item;
        }
      });
      Object.values(lastPhotoPerIndex).forEach(item => {
        const base64 = item.photo.split(',')[1];
        const ext = item.photo.startsWith('data:image/png') ? 'png' : 'jpg';
        zip.file(`photo_${item.index + 1}_${item.timestamp}.${ext}`, base64, { base64: true });
      });
      // Tambahkan hasil grid juga (render ulang grid ke canvas)
      const gridBlob = await handleDownloadGridBlob();
      if (gridBlob) {
        zip.file(`photo-grid-${new Date().getTime()}.jpg`, gridBlob);
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `all-photos-${new Date().getTime()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      if (onDownload) onDownload();
    } catch (err) {
      alert('Gagal mengunduh semua foto.');
    }
  };

  // Fungsi untuk render grid ke blob (mirip handleDownload, tapi return blob saja)
  const handleDownloadGridBlob = async () => { 
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const container = resultContainerRef.current;
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const photoElements = container.querySelectorAll('.result-photo img');
      const photoPromises = Array.from(photoElements).map(img => {
        return new Promise((resolve) => {
          const tempImg = new Image();
          tempImg.crossOrigin = 'anonymous';
          tempImg.onload = () => {
            const rect = img.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const x = (rect.left - containerRect.left) * (canvas.width / containerRect.width);
            const y = (rect.top - containerRect.top) * (canvas.height / containerRect.height);
            const width = rect.width * (canvas.width / containerRect.width);
            const height = rect.height * (canvas.height / containerRect.height);
            ctx.save();
            ctx.translate(x + width, y);
            ctx.scale(-1, 1);
            ctx.drawImage(tempImg, 0, 0, width, height);
            ctx.restore();
            resolve();
          };
          tempImg.src = img.src;
        });
      });
      await Promise.all(photoPromises);
      return await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95));
    } catch {
      return null;
    }
  };

  // Ambil hasil akhir (foto terakhir per slot)
  const lastPhotoPerIndex = {};
  allPhotosHistory.forEach(item => {
    if (item.photo) {
      lastPhotoPerIndex[item.index] = item;
    }
  });
  const finalPhotos = Object.values(lastPhotoPerIndex);

  // Handler untuk menampilkan gallery
  const handleShowGallery = () => {
    setShowGallery(true);
  };
  const handleCloseGallery = () => {
    setShowGallery(false);
  };

  // Handler untuk membuat video dari foto terakhir per slot
  const handleShowVideo = async () => {
    // Buat video dari finalPhotos
    const images = finalPhotos.map(item => item.photo);
    // Buat video dari images (pakai Web APIs)
    const videoBlob = await createVideoFromImages(images);
    if (videoBlob) {
      setVideoUrl(URL.createObjectURL(videoBlob));
      setShowVideo(true);
    }
  };
  const handleCloseVideo = () => {
    setShowVideo(false);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
  };

  // Fungsi untuk membuat video grid blob
  const createVideoGridBlob = async () => {
    try {
      const validVideos = recordedVideos.filter(v => v);
      
      if (validVideos.length === 0) {
        return null;
      }

      // Buat video grid gabungan menggunakan canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set ukuran canvas sesuai dengan container
      const container = resultContainerRef.current;
      if (!container) {
        return null;
      }
      
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      
      // Background putih
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Buat video element untuk setiap slot
      const videoElements = [];
      const videoPromises = [];
      
      // Buat video element untuk semua slot (termasuk yang kosong)
      for (let i = 0; i < gridCount; i++) {
        if (recordedVideos && recordedVideos[i]) {
          const promise = new Promise((resolve) => {
            const video = document.createElement('video');
            video.src = recordedVideos[i];
            video.muted = true;
            video.loop = true;
            video.playsInline = true;
            video.onloadedmetadata = () => {
              videoElements[i] = video;
              resolve();
            };
            video.onerror = () => {
              videoElements[i] = null;
              resolve();
            };
          });
          videoPromises.push(promise);
        } else {
          videoElements[i] = null;
        }
      }

      if (videoPromises.length > 0) {
        await Promise.all(videoPromises);
      }

      // Mulai recording canvas
      const stream = canvas.captureStream(30); // 30 FPS
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      
      return new Promise((resolve) => {
        const chunks = [];
        
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          resolve(blob);
        };

        // Mulai recording
        mediaRecorder.start();

        // Render video grid ke canvas
        const renderFrame = () => {
          try {
            // Clear canvas
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Render setiap video sesuai layout
            videoElements.forEach((video, index) => {
              if (video && video.readyState >= 2) {
                const slotDiv = container.children[index];
                if (slotDiv) {
                  const rect = slotDiv.getBoundingClientRect();
                  const containerRect = container.getBoundingClientRect();
                  
                  const x = (rect.left - containerRect.left) * (canvas.width / containerRect.width);
                  const y = (rect.top - containerRect.top) * (canvas.height / containerRect.height);
                  const width = rect.width * (canvas.width / containerRect.width);
                  const height = rect.height * (canvas.height / containerRect.height);
                  
                  ctx.save();
                  ctx.translate(x + width, y);
                  ctx.scale(-1, 1);
                  ctx.drawImage(video, 0, 0, width, height);
                  ctx.restore();
                }
              }
            });

            if (mediaRecorder.state === 'recording') {
              requestAnimationFrame(renderFrame);
            }
          } catch (error) {
            console.error('Error in renderFrame:', error);
          }
        };

        // Mulai render dan play semua video
        videoElements.forEach(video => {
          if (video) {
            video.play().catch(e => console.error('Error playing video:', e));
          }
        });

        renderFrame();

        // Stop recording setelah 5 detik
        setTimeout(() => {
          try {
            if (mediaRecorder.state === 'recording') {
              mediaRecorder.stop();
            }
            videoElements.forEach(video => {
              if (video) {
                video.pause();
                video.src = '';
              }
            });
          } catch (error) {
            console.error('Error stopping mediaRecorder:', error);
          }
        }, 5000);
      });

    } catch (error) {
      console.error('Error creating video grid blob:', error);
      return null;
    }
  };

  // Fungsi download video grid
  const handleDownloadVideoGrid = async () => {
    let container = null;
    let mediaRecorder = null;
    
    try {
      const validVideos = recordedVideos.filter(v => v);
      
      if (validVideos.length === 0) {
        alert('Tidak ada video yang tersedia untuk diunduh.');
        return;
      }

      // 1. Buat container grid sementara
      container = document.createElement('div');
      container.className = `result-container ${getLayoutClass()}`;
      Object.assign(container.style, {
        position: 'absolute', left: '-9999px', top: '-9999px',
        width: '600px', height: '1500px', background: '#f8f9fa',
        borderRadius: '8px', overflow: 'hidden', display: 'grid',
        gap: '20px', padding: '20px', placeItems: 'center', zoom: '50%'
      });
      document.body.appendChild(container);

      // 2. Tambahkan slot video ke grid
      for (let i = 0; i < gridCount; i++) {
        const slotDiv = document.createElement('div');
        slotDiv.className = 'result-photo';
        Object.assign(slotDiv.style, {
          width: '100%', height: '100%', overflow: 'hidden',
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        });
        
        if (recordedVideos && recordedVideos[i]) {
          const video = document.createElement('video');
          video.src = recordedVideos[i];
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.autoplay = true;
          Object.assign(video.style, {
            width: 'auto', height: 'auto', objectFit: 'contain',
            transform: 'scaleX(-1)', maxWidth: '900px', maxHeight: '100%'
          });
          slotDiv.appendChild(video);
        } else {
          // Placeholder untuk slot kosong
          const placeholder = document.createElement('div');
          placeholder.style.cssText = 'width: 100%; height: 100%; background: #eee; display: flex; align-items: center; justify-content: center;';
          placeholder.textContent = 'No Video';
          slotDiv.appendChild(placeholder);
        }
        container.appendChild(slotDiv);
      }

      // 3. Tunggu video siap
      const videos = Array.from(container.querySelectorAll('video'));
      if (videos.length > 0) {
        await Promise.all(videos.map(
          v => new Promise(res => {
            if (v.readyState >= 2) {
              res();
            } else {
              v.onloadeddata = res;
              v.onerror = res; // Handle error case
            }
          })
        ));
      }

      // 4. Rekam canvas dari container
      const rect = container.getBoundingClientRect();
      const canvas = document.createElement('canvas');
      canvas.width = rect.width;
      canvas.height = rect.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const stream = canvas.captureStream(30);
      mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      const chunks = [];
      
      mediaRecorder.ondataavailable = e => { 
        if (e.data.size > 0) chunks.push(e.data); 
      };
      
      mediaRecorder.onstop = () => {
        try {
          const blob = new Blob(chunks, { type: 'video/webm' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `video-grid-${indukName || 'session'}-${new Date().getTime()}.webm`;
          document.body.appendChild(link);
          link.click();
          
          // Cleanup dengan pengecekan
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error in mediaRecorder.onstop:', error);
        }
      };

      mediaRecorder.start();

      // 5. Render frame video ke canvas
      function renderFrame() {
        try {
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          const videos = Array.from(container.querySelectorAll('video'));
          videos.forEach((video, i) => {
            if (video && video.readyState >= 2) {
              const slotDiv = container.children[i];
              if (slotDiv) {
                const r = slotDiv.getBoundingClientRect();
                const c = container.getBoundingClientRect();
                const x = (r.left - c.left) * (canvas.width / c.width);
                const y = (r.top - c.top) * (canvas.height / c.height);
                const w = r.width * (canvas.width / c.width);
                const h = r.height * (canvas.height / c.height);
                ctx.save();
                ctx.translate(x + w, y);
                ctx.scale(-1, 1);
                ctx.drawImage(video, 0, 0, w, h);
                ctx.restore();
              }
            }
          });
          
          if (mediaRecorder && mediaRecorder.state === 'recording') {
            requestAnimationFrame(renderFrame);
          }
        } catch (error) {
          console.error('Error in renderFrame:', error);
        }
      }
      
      renderFrame();

      // Stop recording setelah 5 detik
      setTimeout(() => {
        try {
          if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
          // Cleanup videos
          videos.forEach(v => { 
            if (v) {
              v.pause(); 
              v.src = '';
            }
          });
        } catch (error) {
          console.error('Error stopping mediaRecorder:', error);
        }
      }, 5000);

    } catch (error) {
      console.error('Error downloading video grid:', error);
      alert('Gagal mengunduh video grid. Silakan coba lagi.');
    } finally {
      // Cleanup container dengan pengecekan
      if (container && document.body.contains(container)) {
        try {
          document.body.removeChild(container);
        } catch (error) {
          console.error('Error removing container:', error);
        }
      }
    }
  };

  // Fungsi membuat video dari array images (dataURL)
  async function createVideoFromImages(images) {
    // Pakai Whammy.js (atau WebCodecs jika tersedia), fallback ke GIF jika tidak bisa video
    // Untuk demo, kita buat GIF pakai gif.js (karena WebCodecs/Whammy tidak built-in)
    // Jika ingin video mp4/webm, perlu library eksternal atau backend
    // Di sini, kita buat GIF sebagai video preview
    // ---
    // Dynamic import gif.js jika belum ada
    if (!window.GIF) {
      await new Promise(resolve => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/gif.js/0.2.0/gif.js';
        script.onload = resolve;
        document.body.appendChild(script);
      });
    }
    return new Promise((resolve, reject) => {
      const gif = new window.GIF({ workers: 2, quality: 10 });
      images.forEach(src => {
        const img = document.createElement('img');
        img.src = src;
        gif.addFrame(img, { delay: 800 });
      });
      gif.on('finished', function(blob) {
        resolve(blob);
      });
      gif.on('error', reject);
      gif.render();
    });
  }

  // Export all slot photos, grid, and gif with custom name
  const handleExportToAssets = async () => {
    const name = indukName || prompt('Masukkan nama induk file (misal: event2024 atau timestamp):');
    if (!name) return;
    setExporting(true);
    try {
      // Export slot photos (final per slot)
      const lastPhotoPerIndex = {};
      allPhotosHistory.forEach(item => {
        if (item.photo) {
          lastPhotoPerIndex[item.index] = item;
        }
      });
      const finalPhotos = Object.values(lastPhotoPerIndex);
      const files = [];
      for (let i = 0; i < finalPhotos.length; i++) {
        const item = finalPhotos[i];
        const base64 = item.photo.split(',')[1];
        const ext = item.photo.startsWith('data:image/png') ? 'png' : 'jpg';
        const link = document.createElement('a');
        link.href = item.photo;
        link.download = `${name}_slot${item.index + 1}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        await new Promise(r => setTimeout(r, 300)); // Delay to ensure download
        files.push(dataURLtoFile(item.photo, `${name}_slot${item.index + 1}.${ext}`));
      }
      // Export grid
      const gridBlob = await handleDownloadGridBlob();
      if (gridBlob) {
        const url = URL.createObjectURL(gridBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name}_grid.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        await new Promise(r => setTimeout(r, 300));
        files.push(dataURLtoFile(gridBlob, `${name}_grid.jpg`));
      }
      // Export GIF (if available)
      if (window.GIF) {
        const images = finalPhotos.map(item => item.photo);
        const gifBlob = await new Promise((resolve, reject) => {
          const gif = new window.GIF({ workers: 2, quality: 10 });
          images.forEach(src => {
            const img = document.createElement('img');
            img.src = src;
            gif.addFrame(img, { delay: 800 });
          });
          gif.on('finished', function(blob) { resolve(blob); });
          gif.on('error', reject);
          gif.render();
        });
        const url = URL.createObjectURL(gifBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name}_gif.gif`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        files.push(dataURLtoFile(gifBlob, `${name}_gif.gif`));
      }
      await uploadAssets(files);
      alert('Export selesai! Silakan pindahkan file ke public/assets.');
    } catch (err) {
      alert('Export gagal: ' + err.message);
    }
    setExporting(false);
  };

  async function uploadAssets(files) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file, file.name));
    await fetch('https://api.laviopaperie.com/api/upload-assets', {
      method: 'POST',
      body: formData,
      mode: 'cors',
      credentials: 'omit',
    });
  }

  function dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while(n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, {type:mime});
  }

  // --- AUTO UPLOAD TO BACKEND WHEN RESULT PAGE SHOWN ---
  useEffect(() => {
    // Hitung finalPhotos di dalam useEffect untuk menghindari dependency issues
    const lastPhotoPerIndex = {};
    allPhotosHistory.forEach(item => {
      if (item.photo) {
        lastPhotoPerIndex[item.index] = item;
      }
    });
    const finalPhotos = Object.values(lastPhotoPerIndex);
    
    // Cek apakah semua kondisi terpenuhi untuk upload
    const shouldUpload = indukName && 
                        finalPhotos.length === gridCount && 
                        finalPhotos.length > 0 && // Pastikan ada foto
                        uploadStatus === 'idle' && 
                        !hasUploaded && 
                        !uploadAttempted &&
                        !uploadComplete;
    
    if (!shouldUpload) {
      console.log('Upload skipped:', { 
        indukName, 
        finalPhotosLength: finalPhotos.length, 
        gridCount, 
        uploadStatus, 
        hasUploaded, 
        uploadAttempted,
        uploadComplete
      });
      return;
    }
    
    console.log('Starting upload process...');
    setUploadAttempted(true); // Set flag untuk mencegah multiple attempts
    setUploadComplete(true); // Set flag bahwa upload akan dimulai
    
    // Simpan state ke localStorage
    try {
      localStorage.setItem(`uploadAttempted_${indukName}`, 'true');
      localStorage.setItem(`uploadComplete_${indukName}`, 'true');
    } catch (error) {
      console.warn('Error saving upload state to localStorage:', error);
    }
    
    (async () => {
      setUploadStatus('uploading');
      try {
        const files = [];
        // Upload photo files (final per slot)
        for (let i = 0; i < gridCount; i++) {
          const item = finalPhotos.find(f => f.index === i);
          if (item && item.photo) {
            const ext = item.photo.startsWith('data:image/png') ? 'png' : 'jpg';
            files.push(dataURLtoFile(item.photo, `${indukName}_${i+1}.${ext}`));
          }
        }
        // Upload video files (if available)
        if (recordedVideos) {
          for (let i = 0; i < gridCount; i++) {
            if (recordedVideos[i]) {
              const response = await fetch(recordedVideos[i]);
              const blob = await response.blob();
              files.push(new File([blob], `${indukName}_video_${i+1}.webm`, { type: 'video/webm' }));
            }
          }
        }
        // Export grid (photo grid)
        const gridBlob = await handleDownloadGridBlob();
        if (gridBlob) {
          files.push(new File([gridBlob], `${indukName}_grid.jpg`, { type: 'image/jpeg' }));
        }
        
        // Export video grid (if videos available)
        if (recordedVideos && recordedVideos.some(v => v)) {
          try {
            const videoGridBlob = await createVideoGridBlob();
            if (videoGridBlob) {
              files.push(new File([videoGridBlob], `${indukName}_video_grid.webm`, { type: 'video/webm' }));
            }
          } catch (error) {
            console.warn('Failed to create video grid:', error);
          }
        }
        if (files.length > 0) {
          await uploadAssets(files);
        }
        
        console.log('Upload completed successfully!');
        setUploadStatus('done');
        setHasUploaded(true); // Set flag bahwa upload sudah dilakukan
        
        // Simpan state ke localStorage
        try {
          localStorage.setItem(`uploadStatus_${indukName}`, 'done');
          localStorage.setItem(`hasUploaded_${indukName}`, 'true');
        } catch (error) {
          console.warn('Error saving upload state to localStorage:', error);
        }
        
        // Set countdown timer untuk refresh page setelah 1 menit
        let timeLeft = 90; // 60 detik
        setCountdown(timeLeft);
        
        // Simpan countdown ke localStorage
        try {
          localStorage.setItem(`countdown_${indukName}`, timeLeft.toString());
        } catch (error) {
          console.warn('Error saving countdown to localStorage:', error);
        }
        
        console.log('Starting countdown timer...');
        setCountdownStarted(true);
        
        // Simpan countdown started ke localStorage
        try {
          localStorage.setItem(`countdownStarted_${indukName}`, 'true');
        } catch (error) {
          console.warn('Error saving countdown started to localStorage:', error);
        }
        
        // Cleanup existing timer if any
        if (window.countdownTimerRef) {
          console.log('Cleaning up existing countdown timer');
          clearInterval(window.countdownTimerRef);
          window.countdownTimerRef = null;
        }
        
        // Start countdown timer dengan setTimeout untuk memastikan state sudah diupdate
        setTimeout(() => {
          console.log('Countdown timer started with', timeLeft, 'seconds');
          
          const countdownTimer = setInterval(() => {
            setCountdown(prevCountdown => {
              const newCountdown = prevCountdown - 1;
              console.log('Countdown:', newCountdown);
              
              // Simpan countdown ke localStorage
              try {
                localStorage.setItem(`countdown_${indukName}`, newCountdown.toString());
              } catch (error) {
                console.warn('Error saving countdown to localStorage:', error);
              }
              
              if (newCountdown <= 0) {
                clearInterval(countdownTimer);
                // Cleanup localStorage
                try {
                  localStorage.removeItem(`uploadStatus_${indukName}`);
                  localStorage.removeItem(`hasUploaded_${indukName}`);
                  localStorage.removeItem(`uploadAttempted_${indukName}`);
                  localStorage.removeItem(`uploadComplete_${indukName}`);
                  localStorage.removeItem(`countdown_${indukName}`);
                  localStorage.removeItem(`countdownStarted_${indukName}`);
                } catch (error) {
                  console.warn('Error cleaning up localStorage:', error);
                }
                console.log('Countdown finished, refreshing page...');
                window.location.reload();
              }
              return newCountdown;
            });
          }, 1000);
          
          // Store timer reference untuk cleanup
          window.countdownTimerRef = countdownTimer;
        }, 100); // Delay kecil untuk memastikan state sudah diupdate
        
      } catch (err) {
        console.error('Upload error:', err);
        setUploadStatus('error');
        setUploadAttempted(false); // Reset flag jika error
        setUploadComplete(false); // Reset flag jika error
        
        // Simpan error state ke localStorage
        try {
          localStorage.setItem(`uploadStatus_${indukName}`, 'error');
          localStorage.removeItem(`uploadAttempted_${indukName}`);
          localStorage.removeItem(`uploadComplete_${indukName}`);
        } catch (error) {
          console.warn('Error saving error state to localStorage:', error);
        }
      }
    })();
    
    // eslint-disable-next-line
  }, [indukName, gridCount, hasUploaded, uploadAttempted, uploadComplete]);

  useEffect(() => {
    if (indukName && gridCount && layout) {
      postPhotoSession({ indukname: indukName, grid: gridCount, layout });
    }
  }, [indukName, gridCount, layout]);

  useEffect(() => {
    if (!initialPhotos && (sessionId || indukName)) {
      // Try to load from localStorage
      try {
        const sid = sessionId || indukName;
        const sessionData = JSON.parse(localStorage.getItem('sessionData'));
        if (sessionData) {
          setLayout(sessionData.layout);
          setGridCount(sessionData.grid);
        }
        const photosArr = JSON.parse(localStorage.getItem('photos')) || [];
        setPhotos(photosArr);
        const historyArr = JSON.parse(localStorage.getItem('allPhotosHistory')) || [];
        setAllPhotosHistory(historyArr);
        // Load recordedVideos as base64 and convert to blob URL
        let videosArr = [];
        if (sessionData && sessionData.grid) {
          for (let i = 0; i < sessionData.grid; i++) {
            const base64 = localStorage.getItem(`recordedVideo_${sid}_${i}`);
            if (base64) {
              // Convert base64 to blob URL
              const arr = base64.split(',');
              const mime = arr[0].match(/:(.*?);/)[1];
              const bstr = atob(arr[1]);
              let n = bstr.length;
              const u8arr = new Uint8Array(n);
              while(n--) u8arr[n] = bstr.charCodeAt(n);
              const blob = new Blob([u8arr], {type: mime});
              const url = URL.createObjectURL(blob);
              videosArr[i] = url;
            } else {
              videosArr[i] = null;
            }
          }
        }
        setRecordedVideos(videosArr);
        setIndukName(sid);
        setLoaded(true);
      } catch (e) {
        setLoaded(true);
      }
    } else {
      setLoaded(true);
    }
  }, [initialPhotos, sessionId, indukName]);

  return (
    <div className="photo-result">
      <h2>Hasil Foto & Video</h2>
      {uploadStatus === 'uploading' && <div style={{color:'#007bff'}}>Mengupload hasil ke server...</div>}
      {uploadStatus === 'done' && (
        <div style={{color:'green', textAlign:'center', padding:'10px', background:'#f0f8f0', borderRadius:'5px', margin:'10px 0'}}>
          <div>Upload selesai! Foto & video bisa diakses di gallery.</div>
          {countdown !== null && (
            <div style={{marginTop:'5px', fontSize:'14px', color:'#666'}}>
              Halaman akan refresh dalam {countdown} detik...
            </div>
          )}
        </div>
      )}
      {uploadStatus === 'error' && <div style={{color:'red'}}>Upload gagal! Coba refresh halaman.</div>}
      
      {/* Toggle untuk switch antara grid foto dan video */}
      <div style={{marginBottom: 16, textAlign: 'center'}}>
        <button 
          onClick={() => {
            setShowVideoGrid(false);
            // Pause semua video saat switch ke foto
            const videoElements = document.querySelectorAll('.result-photo video');
            videoElements.forEach(video => {
              video.pause();
            });
          }} 
          style={{
            padding: '8px 16px',
            margin: '0 4px',
            background: !showVideoGrid ? '#007bff' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          Grid Foto
        </button>
        <button 
          onClick={() => {
            setShowVideoGrid(true);
            // Resume video setelah switch
            setTimeout(() => {
              const videoElements = document.querySelectorAll('.result-photo video');
              videoElements.forEach(video => {
                if (video.paused) {
                  video.play().catch(err => console.log('Resume failed:', err));
                }
              });
            }, 100);
          }} 
          style={{
            padding: '8px 16px',
            margin: '0 4px',
            background: showVideoGrid ? '#007bff' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          Grid Video
        </button>
      </div>

      {/* Grid Foto */}
      {!showVideoGrid && (
        <div ref={resultContainerRef} className={`result-container ${getLayoutClass()}`}> 
          {Array.from({ length: gridCount }).map((_, i) => (
            <div key={i} className="result-photo">
              {photos[i] ? (
                <img src={photos[i]} alt={`Result ${i + 1}`} />
              ) : (
                <div style={{ width: '100%', height: '100%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  No Photo
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Grid Video */}
      {showVideoGrid && (
      <div ref={resultContainerRef} className={`result-container ${getLayoutClass()}`}>
          {Array.from({ length: gridCount }).map((_, i) => (
            <div key={i} className="result-photo">
              {recordedVideos && recordedVideos[i] ? (
                  <video 
                    src={recordedVideos[i]} 
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload={showVideoGrid ? "auto" : "none"}
                    onLoadStart={(e) => {
                      // Loading state
                      e.target.style.opacity = '0.7';
                      const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                      if (loadingIndicator) loadingIndicator.style.display = 'flex';
                    }}
                    onCanPlay={(e) => {
                      // Video siap untuk play
                      e.target.style.opacity = '1';
                      const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                      if (loadingIndicator) loadingIndicator.style.display = 'none';
                      e.target.play().catch(err => console.log('Auto play failed:', err));
                    }}
                    onLoadedData={(e) => {
                      // Pastikan video mulai play setelah data loaded
                      e.target.style.opacity = '1';
                      const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                      if (loadingIndicator) loadingIndicator.style.display = 'none';
                      e.target.play().catch(err => console.log('Auto play failed:', err));
                    }}
                    onEnded={(e) => {
                      // Restart video saat selesai untuk smooth loop
                      e.target.currentTime = 0;
                      e.target.play().catch(err => console.log('Restart failed:', err));
                    }}
                    onError={(e) => {
                      console.error('Video error:', e);
                      // Hide loading indicator on error
                      const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                      if (loadingIndicator) loadingIndicator.style.display = 'none';
                      // Show error state
                      e.target.style.opacity = '0.5';
                    }}
                    style={{
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      transform: 'scaleX(-1)',
                      maxWidth: '900px',
                      maxHeight: '100%',
                      transition: 'opacity 0.3s ease'
                    }}
                  />
              ) : (
                <div style={{ width: '100%', height: '100%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  No Video
                </div>
              )}
              {/* Loading indicator */}
              <div className="loading-indicator">
                Loading...
              </div>
          </div>
        ))}
      </div>
      )}

      <div className="result-actions">
        <button onClick={handleDownload} className="download-btn">Unduh Foto</button>
        <button onClick={handleDownloadAllZip} className="download-btn">Unduh Semua Foto (ZIP)</button>
        {showVideoGrid && (
          <button onClick={handleDownloadVideoGrid} className="download-btn">Unduh Video Grid (ZIP)</button>
        )}
        <button className="share-btn">Bagikan</button>
        <button onClick={handleShowGallery} className="gallery-btn">Lihat Semua Hasil</button>
        <button onClick={handleShowVideo} className="video-btn">Jadikan Video</button>
        <button onClick={handleExportToAssets} className="export-btn" disabled={exporting}>{exporting ? 'Exporting...' : 'Export to Assets'}</button>
      </div>
      {/* Modal Gallery */}
      {showGallery && (
        <div className="modal-gallery" style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'#000a',zIndex:1000,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',padding:24,borderRadius:8,maxWidth:600,width:'90vw',maxHeight:'100vh',overflowY:'auto'}}>
            <h3>Semua Hasil Foto (Akhir per Slot)</h3>
            <div style={{display:'flex',flexWrap:'wrap',gap:8,justifyContent:'center'}}>
              {finalPhotos.map((item, idx) => (
                <div key={idx} style={{margin:4}}>
                  <img src={item.photo} alt={`Final ${item.index+1}`} style={{width:120,borderRadius:4}} />
                  <div style={{textAlign:'center'}}>Slot {item.index+1}</div>
                </div>
              ))}
            </div>
            <h4 style={{marginTop:24}}>Hasil Grid Akhir</h4>
            <div style={{margin:'12px 0'}}>
              <div className={`result-container ${getLayoutClass()}`} style={{background:'#eee',padding:8}}>
                {photos.map((photo, index) => (
                  <div key={index} className="result-photo">
                    <img src={photo} alt={`Result ${index + 1}`} style={{width:80}} />
                  </div>
                ))}
              </div>
            </div>
            <button onClick={handleCloseGallery} style={{marginTop:16}}>Tutup</button>
          </div>
        </div>
      )}
      {/* Modal Video */}
      {showVideo && videoUrl && (
        <div className="modal-video" style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'#000a',zIndex:1000,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',padding:24,borderRadius:8,maxWidth:600,width:'90vw',maxHeight:'100vh',overflowY:'auto',textAlign:'center'}}>
            <h3>Video dari Hasil Foto</h3>
            <video src={videoUrl} controls autoPlay loop style={{maxWidth:'100%',borderRadius:8}} />
            <div>
              <button onClick={handleCloseVideo} style={{marginTop:16}}>Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

async function postPhotoSession({ indukname, grid, layout }) {
  await fetch('https://api.laviopaperie.com/api/photo-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ indukname, grid, layout }),
  });
}

export default PhotoResult; 